import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Typography, Drawer } from 'antd';
import { 
  DashboardOutlined, 
  UserOutlined, 
  SettingOutlined, 
  LogoutOutlined,
  MenuOutlined,
  ShoppingOutlined,
  TeamOutlined,
  MailOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import './Navigation.css';

const { Header } = Layout;
const { Text } = Typography;

const Navigation = () => {
  const navigate = useNavigate();
  const { user, logout, hasModuleAccess } = useAuth();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showMobileMenu = () => {
    setMobileMenuVisible(true);
  };

  const hideMobileMenu = () => {
    setMobileMenuVisible(false);
  };

  if (!user) {
    return null;
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      onClick: handleLogout
    }
  ];

  const mainMenuItems = [
    ...(hasModuleAccess('dashboard') ? [{
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard" onClick={hideMobileMenu}>Дашборд</Link>
    }] : []),
    ...(hasModuleAccess('products') ? [{
      key: 'products',
      icon: <ShoppingOutlined />,
      label: <Link to="/products" onClick={hideMobileMenu}>Товары</Link>
    }] : []),
    ...(hasModuleAccess('clients') ? [{
      key: 'clients',
      icon: <TeamOutlined />,
      label: <Link to="/clients" onClick={hideMobileMenu}>Клиенты</Link>
    }] : []),
    ...(hasModuleAccess('admin') ? [{
      key: 'mailings',
      icon: <MailOutlined />,
      label: <Link to="/mailings" onClick={hideMobileMenu}>Рассылки</Link>
    }] : []),
    ...(hasModuleAccess('admin') ? [{
      key: 'account-deletions',
      icon: <DeleteOutlined />,
      label: <Link to="/account-deletions" onClick={hideMobileMenu}>Удаления</Link>
    }] : []),
    ...(hasModuleAccess('admin') ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: <Link to="/settings" onClick={hideMobileMenu}>Настройки</Link>
    }] : [])
  ];

  return (
    <>
      <Header style={{ 
        background: '#fff', 
        padding: '0 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        maxWidth: '100vw',
        overflow: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          height: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Логотип */}
          <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
            <Link to={hasModuleAccess('dashboard') ? "/dashboard" : "/profile"} style={{ textDecoration: 'none' }}>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                Limit Project
              </Text>
            </Link>
          </div>

          {/* Десктопное меню - скрыто на мобильных */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            flex: 1, 
            justifyContent: 'center', 
            maxWidth: '60%'
          }} className="desktop-menu">
            <Menu
              mode="horizontal"
              selectedKeys={[]}
              items={mainMenuItems}
              style={{ 
                border: 'none', 
                background: 'transparent',
                lineHeight: '64px',
                flex: 1,
                justifyContent: 'center',
                minWidth: 'auto',
                overflow: 'visible'
              }}
              className="main-navigation-menu"
              overflowedIndicator={null}
              inlineCollapsed={false}
            />
          </div>

          {/* Правая часть - пользователь и мобильное меню */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            flex: '0 0 auto'
          }}>
            {/* Информация о пользователе - скрыта на мобильных */}
            <div className="desktop-user-info">
              <Space>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {user?.name || user?.email}
                </Text>
                <Button 
                  size="small" 
                  type="primary" 
                  style={{ borderRadius: '12px', fontSize: '12px' }}
                >
                  {user?.role}
                </Button>
              </Space>
            </div>
            
            {/* Аватар пользователя */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Avatar 
                size="default" 
                src={user?.avatar_url}
                icon={<UserOutlined />}
                style={{ cursor: 'pointer' }}
              />
            </Dropdown>

            {/* Кнопка мобильного меню */}
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={showMobileMenu}
              className="mobile-menu-button"
            />
          </div>
        </div>
      </Header>

      {/* Мобильное выдвижное меню */}
      <Drawer
        title="Меню"
        placement="left"
        onClose={hideMobileMenu}
        open={mobileMenuVisible}
        width={280}
        bodyStyle={{ padding: 0 }}
        className="mobile-drawer"
      >
        <div style={{ padding: '16px 0' }}>
          {/* Информация о пользователе в мобильном меню */}
          <div style={{ 
            padding: '16px 24px', 
            borderBottom: '1px solid #f0f0f0',
            marginBottom: '16px'
          }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Avatar 
                size="large" 
                src={user?.avatar_url}
                icon={<UserOutlined />}
              />
              <div>
                <Text strong style={{ display: 'block' }}>
                  {user?.name || user?.email}
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {user?.role}
                </Text>
              </div>
            </Space>
          </div>

          {/* Мобильное меню */}
          <Menu
            mode="vertical"
            selectedKeys={[]}
            items={mainMenuItems}
            style={{ 
              border: 'none',
              background: 'transparent'
            }}
          />
        </div>
      </Drawer>

    </>
  );
};

export default Navigation;
